import { supabase } from './supabaseClient';
import { resolveApiBaseUrl } from './apiBaseUrl';

const API_BASE_URL = resolveApiBaseUrl();
const API_TIMEOUT_MS = 15000;

const getErrorMessage = (errorData) => {
  if (typeof errorData?.detail === 'string') return errorData.detail;
  if (typeof errorData?.message === 'string') return errorData.message;
  return 'API request failed';
};

const apiClient = async (endpoint, options = {}) => {
  const { data: { session } } = await supabase.auth.getSession();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

  const headers = {
    'Content-Type': 'application/json',
    ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {}),
    ...options.headers,
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(getErrorMessage(errorData));
      error.status = response.status;
      error.response = { data: errorData, status: response.status };
      throw error;
    }

    if (response.status === 204) {
      return null;
    }
    return response.json().catch(() => null);
  } catch (error) {
    if (error.name === 'AbortError') {
      const timeoutError = new Error('Request timed out. Please try again.');
      timeoutError.status = 408;
      timeoutError.response = {
        data: { detail: 'Request timed out. Please try again.' },
        status: 408,
      };
      throw timeoutError;
    }

    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const getSubscription = () => {
  return apiClient('/api/billing/subscription');
};

export const cancelSubscription = () => {
  return apiClient('/api/billing/cancel', {
    method: 'POST'
  });
};

const getAppOrigin = () => {
  if (typeof window === 'undefined') return undefined;

  const origin = window.location?.origin;
  return typeof origin === 'string' && origin.trim() ? origin : undefined;
};

export const createCheckout = (plan) => {
  const appOrigin = getAppOrigin();

  return apiClient('/api/billing/create-checkout', {
    method: 'POST',
    headers: appOrigin ? { 'X-App-Origin': appOrigin } : undefined,
    body: JSON.stringify({ plan }),
  });
};

export const extractTier = (data) => {
  if (!data) return 'free';
  if (typeof data === 'string') return data.toLowerCase().trim();

  // Handle case where response might be wrapped in { data: { ... } }
  const source = data.data || data;

  const tier = source.subscription_plan ||
               source.plan ||
               source.tier ||
               source.subscription_tier ||
               source.subscription_type ||
               'free';

  return String(tier).toLowerCase().trim();
};
